/**
 * The 1099 floor — total paid per payee per rail per currency for a tax year,
 * exportable (AC-8).
 *
 * **It does not load itself.** `WHERE YEAR(paid_at) = ?` is non-sargable and
 * there is no `paid_at` index, so every call scans and groups the whole
 * `expert_payments` table. Paging (ENG-5624's api half) bounds the response, not
 * the work. This runs at compliance-report frequency by design, so it is behind
 * an explicit action and is never polled, never auto-refreshed, and never
 * re-fetched on focus.
 *
 * **This is a floor, not a filing.** Reversals are not modeled anywhere on the
 * platform, so these totals overstate until someone hand-reconciles them; and
 * the year buckets on `YEAR(paid_at)` in UTC, which can differ from a local tax
 * year for a payment made on a year boundary. Both are stated on the screen
 * rather than left for whoever exports the CSV to discover.
 *
 * **The export is never allowed to be short.** A page that failed to arrive is
 * invisible in a table and invisible in the CSV that follows it, and nothing
 * downstream of a 1099 export re-checks the row count. So a failed page says so,
 * the loaded count is shown against the server's total, and Export is held while
 * either says there is more to come.
 */
import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';

import Button from '@/componentsv2/Button';

import { downloadCsv, toCsv } from '../csv';
import { describeLedgerRunError, describePagingError } from '../errors';
import { formatMoney } from '../format';
import { EXPERT_PAYMENT_LEDGER } from '../operations';

const PAGE_SIZE = 200;

const RAIL_LABEL: Record<string, string> = {
  stripe_transfer: 'Stripe transfer',
  manual: 'Manual',
};

export default function LedgerView(): JSX.Element {
  const [year, setYear] = useState(String(new Date().getUTCFullYear()));
  const [loadedYear, setLoadedYear] = useState<number | null>(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const [load, { data, loading, error, fetchMore }] = useLazyQuery(EXPERT_PAYMENT_LEDGER, {
    // network-only: a ledger read is a compliance artifact, and answering it
    // from a cache that may predate this morning's payout run would be the
    // wrong kind of fast.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.expertPaymentLedger;
  const rows = page?.edges.map((e) => e.node) ?? [];
  const total = page?.pageInfo.total ?? null;
  const parsedYear = /^\d{4}$/.test(year) ? Number(year) : null;
  const hasMore = !reachedEnd && (page?.pageInfo.hasNextPage ?? false);

  function run() {
    if (parsedYear === null) return;
    setReachedEnd(false);
    setLoadMoreError(null);
    setLoadedYear(parsedYear);
    void load({ variables: { year: parsedYear, first: PAGE_SIZE } });
  }

  async function loadMore() {
    const cursor = page?.edges.at(-1)?.cursor;
    if (!cursor || parsedYear === null) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const res = await fetchMore({
        variables: { year: parsedYear, first: PAGE_SIZE, after: cursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.expertPaymentLedger) return prev;
          return {
            ...prev,
            expertPaymentLedger: {
              ...fetchMoreResult.expertPaymentLedger,
              edges: [
                ...prev.expertPaymentLedger.edges,
                ...fetchMoreResult.expertPaymentLedger.edges,
              ],
            },
          };
        },
      });
      if (!res.data?.expertPaymentLedger?.pageInfo.hasNextPage) setReachedEnd(true);
    } catch (err) {
      // `useLazyQuery`'s `error` covers the initial read only — a `fetchMore`
      // rejection never reaches the banner below, so without this the page
      // simply never arrives and the table looks complete.
      setLoadMoreError(describePagingError(err));
    } finally {
      setLoadingMore(false);
    }
  }

  function exportCsv() {
    // `hasMore` is a hard refusal, not just a disabled button: a short 1099
    // export is wrong in a way nothing downstream of this screen re-checks.
    if (loadedYear === null || rows.length === 0 || hasMore) return;
    // Minor units as well as the formatted figure: whoever imports this into QBO
    // needs an unambiguous integer, and a currency column beside it so no two
    // denominations can be summed by accident.
    // payee_name sits beside the id, never instead of it: a 1099 is filed
    // against a person, but the id is the identifier and the only thing that
    // joins back to this platform. An unresolvable payee exports as an empty
    // name rather than dropping the row — the money was still paid.
    const csv = toCsv(
      [
        'payee_user_id',
        'payee_name',
        'year',
        'rail',
        'currency',
        'total_minor_units',
        'total',
        'payment_count',
      ],
      rows.map((r) => [
        r.payee,
        r.payeeIdentity?.name ?? '',
        r.year,
        r.rail,
        r.currency,
        r.total.cents,
        formatMoney(r.total),
        r.count,
      ])
    );
    downloadCsv(`expert-payments-ledger-${loadedYear}.csv`, csv);
  }

  return (
    <section className="flex flex-col gap-16">
      <header className="flex flex-col gap-4">
        <h2 className="text-on-surface hd-5">Paid totals by expert</h2>
        <p className="text-on-surface-muted body-2">
          One row per expert, rail and currency for a calendar year — the floor a 1099 filing is
          built from. This read scans the whole payments table, so it runs only when asked.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-8">
        <div className="flex flex-col gap-6">
          <label htmlFor="ledger-year" className="text-on-surface-muted util-caption">
            Tax year (UTC)
          </label>
          <input
            id="ledger-year"
            inputMode="numeric"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="min-h-[44px] w-[120px] rounded-md border border-surface-border bg-surface-card px-12 py-10 text-on-surface focus:border-brand-primary focus:outline-none"
          />
        </div>
        <Button onClick={run} loading={loading} disabled={loading || parsedYear === null}>
          Run report
        </Button>
        {rows.length > 0 ? (
          <Button variant="secondary" onClick={exportCsv} disabled={hasMore}>
            Export CSV
          </Button>
        ) : null}
      </div>

      {rows.length > 0 ? (
        <div className="flex flex-col gap-4">
          <p className="text-on-surface-muted body-2">
            {total !== null
              ? `Showing ${rows.length.toLocaleString()} of ${total.toLocaleString()} rows.`
              : `Showing ${rows.length.toLocaleString()} rows.`}
          </p>
          {hasMore ? (
            <p className="text-on-surface-muted body-2">
              Export is held until the rest are loaded — a CSV taken now would be short, and nothing
              after this screen would catch it.
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="rounded-md border border-red-700/40 bg-red-50 p-16 text-red-900 body-2 dark:bg-red-900/15 dark:text-red-200">
          {describeLedgerRunError(error)}
        </p>
      ) : null}

      {loadedYear !== null && !loading && rows.length === 0 && !error ? (
        <p className="rounded-md border border-surface-border bg-surface-card p-16 text-on-surface-muted body-2">
          No payments were paid out in {loadedYear}.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <>
          <p className="text-on-surface-muted body-2">
            These totals are a floor. Reversals are not tracked on the platform, so a payment
            reversed outside it is still counted here — reconcile before filing. Years bucket on
            UTC, so a payout made on a year boundary may fall in a different year than it does
            locally.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-surface-border">
                  {['Expert', 'Rail', 'Currency', 'Payments', 'Total'].map((h) => (
                    <th key={h} scope="col" className="px-12 py-8 text-on-surface-muted subtitle-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  // Currency is part of the key, not decoration: one expert can
                  // hold two rows on one rail that differ only by it.
                  <tr
                    key={`${r.payee}|${r.rail}|${r.currency}`}
                    className="border-b border-surface-border"
                  >
                    {/* Name over id, matching the queue's Payee cell. The id
                        stays visible: it is what the operator pastes into the
                        queue's payee filter, and what a null name falls back to. */}
                    <td className="px-12 py-12 text-on-surface body-2">
                      {r.payeeIdentity?.name ? (
                        <span className="flex flex-col">
                          <span>{r.payeeIdentity.name}</span>
                          <span className="text-on-surface-muted util-caption">{r.payee}</span>
                        </span>
                      ) : (
                        r.payee
                      )}
                    </td>
                    <td className="px-12 py-12 text-on-surface body-2">
                      {RAIL_LABEL[r.rail] ?? r.rail}
                    </td>
                    <td className="px-12 py-12 text-on-surface body-2">{r.currency}</td>
                    <td className="px-12 py-12 text-on-surface body-2">{r.count}</td>
                    <td className="px-12 py-12 text-on-surface body-2">{formatMoney(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore ? (
            <div className="flex flex-col gap-8">
              <Button
                variant="secondary"
                onClick={() => void loadMore()}
                loading={loadingMore}
                disabled={loadingMore}
              >
                Load more
              </Button>
              {loadMoreError !== null ? (
                <p role="alert" className="text-red-900 body-2 dark:text-red-200">
                  {loadMoreError}
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
